import '@ag-grid-community/styles/ag-grid.css'
import '@ag-grid-community/styles/ag-theme-material.css'
import { ModuleRegistry } from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {AgGridVue} from "@ag-grid-community/vue";
import * as XLSX from 'xlsx'
import {CsvExportModule} from "@ag-grid-community/csv-export";


ModuleRegistry.registerModules([
    CsvExportModule,
    ClientSideRowModelModule
])

export default {
    name: 'AGDataTable',
    components: {
        AgGridVue,
    },

    props: {
        headers: [],
        rows: [],
        autoScaling: false,
        paginationPageOptions: [],
        disableSelection: true
    },

    emits: ['ready', 'new-row-selected'],

    data() {
        return {
            gridApi: null,
            columnApi: null,
            // Properties in defined in 'defaultColDef' will be applied to all columns unless specified for that column
            defaultColDef: {
                filter: true,
                resizable: true,
                sortable: true,
                flex: 1,
                suppressMenu: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true
                },
                cellClassRules: {
                    'improper-cell': 'x == undefined'
                }
            },
            paginationPageSizeSelector: [100, 200, 500],
            rowHeight: 30,
            rowData: null,
            rowSelection: 'multiple',
            enableCellTextSelection: false,
            uniqueId: new Date().getTime(), // used to select the correct paging-panel with querySelector, in case of multiple tables
        }
    },

    computed: {
        paginationPageOption() {
            return this.paginationPageOptions ?? this.paginationPageSizeSelector
        },

        noClick () {
            return this.disableSelection ?? false
        }
    },

    methods: {
        onGridReady (params) {
            this.gridApi = params.api
            this.gridApi?.setGridOption('domLayout', 'autoHeight')
            this.columnApi = params.columnApi
            this.insertButtons()
            this.$emit('ready', params)
        },

        /**
         * Emits parameters of the selected row (node), including rowIndex, data, etc.
         * Emitted object.multiple is used in multi-select contexts, object.single is used in single-select contexts (where 'object' is the emitted object).
         */
        getSelectedRowData () {
            // rowSelectedEvent is needed for multi-select scenarios, singleSelectedNode is needed for single-select scenarios. They're slightly different objects.
            if (window.getSelection()?.toString() !== '') {
                this.gridApi?.deselectAll()
                return
            }

            const selectedRows = this.gridApi?.getSelectedRows()
            this.$emit('new-row-selected', { multiple: selectedRows, single: selectedRows[0] })
        },

        deselectAll () {
            //@ts-expect-error: next-line
            this.gridApi?.deselectAll()
        },
        exportCsv() {
            try {
                this.gridApi.exportDataAsCsv()
            }
            catch (e) {
                console.error('An error occurred when exporting the table to CSV')
            }
        },

        exportXlsx() {
            const dataAsCsv = this.gridApi.getDataAsCsv({
                processCellCallback: params => {
                    const value = params.value;
                    if (Array.isArray(value)) {
                        return value.join(', '); // ensures arrays stay as single cells
                    }
                    return value;
                }
            });

            const data = dataAsCsv
                .split("\n")
                .map(row => {
                    // Use a smarter CSV split that respects quoted commas
                    return row.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)?.map(cell =>
                        cell.replace(/^"|"$/g, "").trim()
                    ) || [];
                });

            const worksheet = XLSX.utils.aoa_to_sheet(data);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = 'table_data.xlsx';
            link.click();
            URL.revokeObjectURL(link.href);
        },


        insertButtons () {
            const footer = document.querySelector(`.table-${this.uniqueId} .ag-paging-panel`);
            if (footer) {
                // Create a wrapper for the existing content
                const contentWrapper = document.createElement('div');
                contentWrapper.classList.add('ag-paging-panel-summary-wrapper');

                // Move the existing children into the wrapper
                while (footer.firstChild) {
                    contentWrapper.appendChild(footer.firstChild);
                }

                // Insert the wrapper back into the footer
                footer.appendChild(contentWrapper);

                // Create the CSV link
                const reset = document.createElement('span');
                reset.innerText = 'Reset columns';
                reset.classList.add('reset-column-link');
                reset.style.cursor = 'pointer';  // Change the cursor to a pointer to indicate it's clickable
                reset.addEventListener('click', () => {
                    this.columnApi.resetColumnState()
                });

                // Create the text section
                const textSection = document.createElement('div');
                textSection.classList.add('export-options');

                // Create the CSV link
                const csvLink = document.createElement('span');
                csvLink.innerText = 'CSV';
                csvLink.classList.add('export-csv-link');
                csvLink.style.cursor = 'pointer';  // Change the cursor to a pointer to indicate it's clickable
                csvLink.addEventListener('click', () => {
                    this.exportCsv();
                });

                // Create the XLSX link
                const xlsxLink = document.createElement('span');
                xlsxLink.innerText = 'XLSX';
                xlsxLink.classList.add('export-xlsx-link');
                xlsxLink.style.cursor = 'pointer';  // Change the cursor to a pointer to indicate it's clickable
                xlsxLink.addEventListener('click', () => {
                    this.exportXlsx();
                });


                // Add some spacing between the links
                const separator1 = document.createTextNode(' / ');
                const separator2 = document.createTextNode(' / ');

                // Append the links and separator to the text section
                textSection.appendChild(reset);
                textSection.appendChild(separator1);
                textSection.appendChild(csvLink);
                textSection.appendChild(separator2);
                textSection.appendChild(xlsxLink);

                // Insert the text section at the beginning of the footer
                footer.insertBefore(textSection, contentWrapper);

            }
        }
    }
}
